import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * TimeInput — a canonical "seconds" number input with an S ↔ Min toggle pill.
 *
 * Canonical storage stays in seconds; the pill just switches display.
 * Any entry is clamped to a 120-minute (7200s) maximum, whichever unit is
 * active. Defaults to Min mode when the initial value ≥ 120s so long
 * durations render as "5" instead of "300".
 *
 * Props:
 *   • valueSeconds  — the current canonical value (number | "" | null)
 *   • onChangeSeconds(secs)  — called with the new canonical value (number | "")
 *   • label         — visible label; unit suffix appended automatically
 *   • testId        — data-testid root; the S/Min pills append -unit-s / -unit-m
 *   • max           — cap in seconds; defaults to 7200
 */
export default function TimeInput({
  valueSeconds,
  onChangeSeconds,
  label = "Time",
  testId = "time",
  max = 7200,
}) {
  const [unit, setUnit] = useState(() => (Number(valueSeconds) || 0) >= 120 ? "m" : "s");
  const display =
    valueSeconds === "" || valueSeconds == null
      ? ""
      : unit === "m"
        ? Number((Number(valueSeconds) / 60).toFixed(2))
        : valueSeconds;

  const onChange = (e) => {
    const raw = e.target.value;
    if (raw === "") { onChangeSeconds(""); return; }
    const v = Number(raw);
    if (Number.isNaN(v)) return;
    const secs = Math.min(
      max,
      Math.max(0, unit === "m" ? Math.round(v * 60) : Math.round(v)),
    );
    onChangeSeconds(secs);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="hs-label">{label} ({unit === "m" ? "min" : "s"})</Label>
        <div className="inline-flex rounded-full border border-line overflow-hidden text-[10px] font-bold uppercase tracking-[0.14em]">
          <button
            type="button"
            onClick={() => setUnit("s")}
            className={`px-2 py-0.5 ${unit === "s" ? "bg-moss text-white" : "bg-white text-ink-3"}`}
            data-testid={`${testId}-unit-s`}
          >S</button>
          <button
            type="button"
            onClick={() => setUnit("m")}
            className={`px-2 py-0.5 ${unit === "m" ? "bg-moss text-white" : "bg-white text-ink-3"}`}
            data-testid={`${testId}-unit-m`}
          >Min</button>
        </div>
      </div>
      <Input
        type="number"
        step={unit === "m" ? "0.5" : "1"}
        min="0"
        max={unit === "m" ? String(max / 60) : String(max)}
        value={display}
        onChange={onChange}
        className="mt-2 h-11 rounded-xl font-data"
        data-testid={testId}
      />
    </div>
  );
}
